import { GATEWAY_BASE_URL } from '../../config/endpoints.ts';

function Code({ children }: { readonly children: string }): JSX.Element {
  return (
    <pre className="my-4 overflow-x-auto rounded-xl bg-ink p-5 font-mono text-[13px] leading-relaxed text-linen">
      <code>{children}</code>
    </pre>
  );
}

function ToolSection({ title, children }: { readonly title: string; readonly children: React.ReactNode }): JSX.Element {
  return (
    <section className="mb-12 pb-8 border-b border-ink/5 last:border-0">
      <h2 className="text-[20px] font-semibold text-ink mb-4">{title}</h2>
      {children}
    </section>
  );
}

export function ToolGuides(): JSX.Element {
  return (
    <article>
      <h1 className="text-[28px] font-semibold text-ink mb-2">工具接入教程</h1>
      <p className="text-ink-muted mb-10">
        主流 AI 编辑器和 CLI 工具都支持自定义 API 端点。只需将 Base URL 指向哪都通，即可透明切换到我们的网关，无需改动任何代码逻辑。
      </p>

      <ToolSection title="Claude Code">
        <p className="text-ink-muted mb-2">
          Anthropic 官方的 CLI 工具。通过环境变量配置：
        </p>
        <Code>{`# 在 shell 配置文件中添加（~/.zshrc 或 ~/.bashrc）
export ANTHROPIC_BASE_URL=${GATEWAY_BASE_URL}
export ANTHROPIC_API_KEY=你的API密钥

# 重新打开终端后直接使用
claude`}</Code>
        <p className="text-ink-muted text-[13px]">
          配置后 Claude Code 的所有请求都会经过哪都通网关，体验与官方完全一致。
        </p>
      </ToolSection>

      <ToolSection title="Cursor">
        <p className="text-ink-muted mb-2">
          AI 编程编辑器。在设置中覆盖 API 地址：
        </p>
        <ol className="list-decimal list-inside text-ink-muted space-y-1.5 mb-3">
          <li>打开 Cursor → Settings → Models</li>
          <li>找到 Claude 或 OpenAI 配置区域</li>
          <li>将 Override OpenAI Base URL 设置为 <code className="bg-butter px-1.5 py-0.5 rounded text-[13px]">{GATEWAY_BASE_URL}/v1</code></li>
          <li>填入你的 API Key</li>
          <li>在模型列表中选择需要的模型</li>
        </ol>
      </ToolSection>

      <ToolSection title="Cline（VS Code 扩展）">
        <p className="text-ink-muted mb-2">
          VS Code 上最流行的 AI 编程扩展之一：
        </p>
        <ol className="list-decimal list-inside text-ink-muted space-y-1.5 mb-3">
          <li>安装 Cline 扩展</li>
          <li>打开 Cline 设置面板</li>
          <li>API Provider 选择 Anthropic</li>
          <li>Base URL 填入 <code className="bg-butter px-1.5 py-0.5 rounded text-[13px]">{GATEWAY_BASE_URL}</code></li>
          <li>填入 API Key，选择模型</li>
        </ol>
      </ToolSection>

      <ToolSection title="Continue（VS Code / JetBrains 扩展）">
        <p className="text-ink-muted mb-2">
          编辑 <code className="bg-butter px-1.5 py-0.5 rounded text-[13px]">~/.continue/config.json</code>：
        </p>
        <Code>{`{
  "models": [{
    "title": "Claude via nadoutong",
    "provider": "anthropic",
    "model": "claude-sonnet-4-6",
    "apiBase": "${GATEWAY_BASE_URL}",
    "apiKey": "你的API密钥"
  }]
}`}</Code>
      </ToolSection>

      <ToolSection title="OpenCode">
        <p className="text-ink-muted mb-2">
          SST 团队的开源 AI CLI 工具，原生支持 Anthropic 协议：
        </p>
        <Code>{`# ~/.opencode/config.yaml
providers:
  nadoutong:
    kind: anthropic
    apiKey: 你的API密钥
    baseURL: ${GATEWAY_BASE_URL}

models:
  default:
    provider: nadoutong
    model: claude-sonnet-4-6`}</Code>
      </ToolSection>

      <ToolSection title="ChatBox">
        <p className="text-ink-muted mb-2">
          桌面端 AI 对话工具：
        </p>
        <ol className="list-decimal list-inside text-ink-muted space-y-1.5 mb-3">
          <li>打开 ChatBox → Settings → AI Model Provider</li>
          <li>选择 Anthropic Claude API</li>
          <li>API Host 填入 <code className="bg-butter px-1.5 py-0.5 rounded text-[13px]">{GATEWAY_BASE_URL}</code></li>
          <li>填入 API Key</li>
          <li>选择需要的模型即可开始对话</li>
        </ol>
      </ToolSection>
    </article>
  );
}
